import React, { useReducer, useContext, useEffect, useCallback, useRef } from 'react';
import { IVideos, IGetPaginatedVideos, IAssignVideos } from 'types';
import { IAppContext, AppContext } from 'appState/appContext';
import { videosAssignmentReducer } from './videosAssignmentReducer';
import { VideosAssignmentActionTypes } from './videosAssignmentActions';
import {
	videosAssignmentInitialState,
	VideosAssignmentContext,
	TActiveItemToDisplay,
	TTypeOfModalWindow,
	IIDsSelectedItems,
	IVideosWithAdditionalProperty,
	TSelectedItem,
	TVideosSortBy,
	IVideosWithAdditionalField,
} from './videosAssignmentContext';
import {
	getPaginatedVideos,
	getCustomersForAssignVideos,
	getRestaurantsByIDsCustomersForVideos,
	getRestaurantsByIDsRestaurantsForVideos,
	assignVideos,
} from 'backend/videos';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { createAppError } from 'utils/helpers';

const VideosAssignmentState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(videosAssignmentReducer, videosAssignmentInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IVideos>, IGetPaginatedVideos>(
		isMounted.current ? getPaginatedVideos : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			videoStudioId: state.selectedStudio,
			videoCategoryId: state.selectedCategory,
			isUnassignedVideos: state.isUnassignedVideos,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByStudios: state.sortBy === 'StudiosA-Z' || state.sortBy === 'StudiosZ-A' ? state.sortBy : null,
			sortByCategories:
				state.sortBy === 'CategoriesA-Z' || state.sortBy === 'CategoriesZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: VideosAssignmentActionTypes.SET_VIDEOS_FOR_ASSIGN,
				payload: {
					videosForAssign: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const changeSearchStringForAllCustomers = useCallback((value: string) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_ALL_CUSTOMERS,
			payload: {
				searchStringForAllCustomers: value,
			},
		});
	}, []);

	const changeSearchStringForRestaurants = useCallback((value: string) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_RESTAURANTS,
			payload: {
				searchStringForRestaurants: value,
			},
		});
	}, []);

	const changeSearchStringForVideos = useCallback((value: string) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_VIDEOS,
			payload: {
				searchStringForVideos: value,
			},
		});
	}, []);

	const setStudio = (value: TSelectedItem) => {
		dispatch({
			type: VideosAssignmentActionTypes.SET_STUDIO,
			payload: {
				selectedStudio: value,
			},
		});
	};

	const setCategory = (value: TSelectedItem) => {
		dispatch({
			type: VideosAssignmentActionTypes.SET_CATEGORY,
			payload: {
				selectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: VideosAssignmentActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TVideosSortBy) => {
		dispatch({
			type: VideosAssignmentActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setIDsSelectedVideos = (value: string[]) => {
		dispatch({
			type: VideosAssignmentActionTypes.SET_IDS_SELECTED_VIDEOS,
			payload: {
				IDsSelectedVideos: value,
			},
		});
	};

	const changeActiveItemToDisplay = (value: TActiveItemToDisplay) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_ACTIVE_ITEM_TO_DISPLAY,
			payload: {
				activeItemToDisplay: value,
			},
		});
	};

	const setShowBreadcrumbs = (value?: boolean) => {
		const isShowBreadcrumbs = value !== undefined ? value : !state.isShowBreadcrumbs;
		dispatch({
			type: VideosAssignmentActionTypes.SET_SHOW_BREADCRUMBS,
			payload: {
				isShowBreadcrumbs,
			},
		});
	};

	const setAllCustomers = async () => {
		try {
			showLoader();
			const response = await getCustomersForAssignVideos();
			dispatch({
				type: VideosAssignmentActionTypes.SET_ALL_CUSTOMERS,
				payload: {
					allCustomers: response.data.customers,
				},
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedCustomers = async (value: IIDsSelectedItems, isUpdateRestaurantsList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateRestaurantsList) {
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: {},
						restaurants: [],
						IDsSelectedRestaurants: {},
						videos: [],
					},
				});
			} else if (isUpdateRestaurantsList) {
				const arrWithCustomerIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsCustomersForVideos(arrWithCustomerIDs);
				const allRestaurantIDs = response.data.customers
					.filter((customer) => customer.restaurants.length)
					.map((customer) => customer.restaurants.map((restaurant) => restaurant.restaurantId))
					.flat();
				const updatedIDsSelectedRestaurants = allRestaurantIDs.reduce<IIDsSelectedItems>((acc, key) => {
					if (key in state.IDsSelectedRestaurants) {
						acc[key] = state.IDsSelectedRestaurants[key];
					}
					return acc;
				}, {});
				const updatedVideos = state.videos.filter((el) => allRestaurantIDs.includes(el.restaurantId));
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: response.data.customers,
						IDsSelectedRestaurants: updatedIDsSelectedRestaurants,
						videos: updatedVideos,
					},
				});
			} else {
				// just to open and close tabs with restaurants.
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: state.restaurants,
						IDsSelectedRestaurants: state.IDsSelectedRestaurants,
						videos: state.videos,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedRestaurants = async (value: IIDsSelectedItems, isUpdateVideosList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateVideosList) {
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: {},
						videos: [],
					},
				});
			} else if (isUpdateVideosList) {
				const arrWithRestaurantIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsRestaurantsForVideos(arrWithRestaurantIDs);
				const IDsRestaurantFromState = state.videos.map((el) => el.restaurantId);
				const IDsRestaurantFromResponse = response.data.restaurants.map((el) => el.restaurantId);
				const filteredVideosFromState = state.videos.filter((el) =>
					IDsRestaurantFromResponse.includes(el.restaurantId),
				);
				const filteredVideosFromResponse = response.data.restaurants
					.filter((el) => !IDsRestaurantFromState.includes(el.restaurantId))
					.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantAddress: el.restaurantAddress,
						isChanged: false,
						videos: el.videos.map((video) => ({ ...video, isAddedDishFromList: false })),
					}));
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						videos: [...filteredVideosFromState, ...filteredVideosFromResponse],
					},
				});
			} else {
				// just to open and close tabs with videos.
				dispatch({
					type: VideosAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						videos: state.videos,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const addSelectedVideosToRestaurant = () => {
		const selectedVideosFromVideoList = state.IDsSelectedVideos.reduce<IVideosWithAdditionalField[]>((acc, id) => {
			const searchVideo = state.videosForAssign.videos.find((el) => el.id === id);
			if (searchVideo) {
				acc.push({
					videoId: searchVideo.id,
					videoName: searchVideo.name,
					iconPath: searchVideo.iconPath,
					customerName: null,
					restaurantAddress: null,
					isAddedDishFromList: true,
				});
			}
			return acc;
		}, []);

		const IDsOfOpenRestaurantTabs = Object.keys(state.IDsSelectedRestaurants).filter(
			(id) => state.IDsSelectedRestaurants[id] === true,
		);

		const updatedVideos = state.videos.map<IVideosWithAdditionalProperty>((restaurant) => {
			if (!IDsOfOpenRestaurantTabs.includes(restaurant.restaurantId)) {
				return restaurant;
			}
			const IDsOfAvailableVideos = restaurant.videos.map((video) => video.videoId);
			const videosForAdding = selectedVideosFromVideoList.filter(
				(video) => !IDsOfAvailableVideos.includes(video.videoId),
			);
			return {
				...restaurant,
				isChanged: true,
				videos: [...restaurant.videos, ...videosForAdding],
			};
		});

		dispatch({
			type: VideosAssignmentActionTypes.SET_VIDEOS,
			payload: {
				videos: updatedVideos,
				IDsSelectedVideos: [],
			},
		});
	};

	const deleteVideoFromRestaurant = (restaurantID: string, videoID: string) => {
		const updatedVideos = state.videos.reduce<IVideosWithAdditionalProperty[]>((acc, restaurant) => {
			if (restaurant.restaurantId === restaurantID) {
				const filteredVideos = restaurant.videos.filter((el) => el.videoId !== videoID);
				acc.push({
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					videos: filteredVideos,
				});
				return acc;
			}
			acc.push(restaurant);
			return acc;
		}, []);
		dispatch({
			type: VideosAssignmentActionTypes.SET_VIDEOS,
			payload: {
				videos: updatedVideos,
				IDsSelectedVideos: state.IDsSelectedVideos,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: VideosAssignmentActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const toggleUnassignedVideos = () => {
		dispatch({
			type: VideosAssignmentActionTypes.TOGGLE_UNASSIGNED_VIDEOS,
		});
	};

	const resetChanges = () => dispatch({ type: VideosAssignmentActionTypes.RESET_CHANGES });

	const sendChanges = async () => {
		try {
			const dataForAssign = state.videos.reduce<IAssignVideos[]>((acc, restaurant) => {
				if (restaurant.isChanged) {
					acc.push({
						restaurantId: restaurant.restaurantId,
						videoIds: restaurant.videos.map((el) => el.videoId),
					});
				}
				return acc;
			}, []);
			showLoader();
			await assignVideos(dataForAssign);
			changeTypeOfModalWindow('success');
		} catch (err) {
			changeTypeOfModalWindow('error');
		} finally {
			hideLoader();
		}
	};

	const updateModuleDataAfterSaving = () => {
		refetch();
		setAllCustomers();
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.selectedStudio,
		state.selectedCategory,
		state.isUnassignedVideos,
		state.sortBy,
	]);

	useEffect(() => {
		if (state.activeItemToDisplay === 'videos' && !state.videos.length) {
			changeActiveItemToDisplay('restaurants');
		} else if (!state.restaurants.length) {
			changeActiveItemToDisplay('customers');
		}
	}, [state.restaurants, state.videos]);

	useEffect(() => {
		setAllCustomers();
	}, []);

	return (
		<VideosAssignmentContext.Provider
			value={{
				changeSearchString,
				changeSearchStringForAllCustomers,
				changeSearchStringForRestaurants,
				changeSearchStringForVideos,
				setCategory,
				setStudio,
				resetSelectedControls,
				setSortBy,
				setIDsSelectedVideos,
				changeActiveItemToDisplay,
				setShowBreadcrumbs,
				setIDsSelectedCustomers,
				setIDsSelectedRestaurants,
				addSelectedVideosToRestaurant,
				deleteVideoFromRestaurant,
				changeTypeOfModalWindow,
				changeCurrentPage,
				toggleUnassignedVideos,
				resetChanges,
				sendChanges,
				updateModuleDataAfterSaving,
				videosForAssign: state.videosForAssign,
				searchString: state.searchString,
				searchStringForAllCustomers: state.searchStringForAllCustomers,
				searchStringForRestaurants: state.searchStringForRestaurants,
				searchStringForVideos: state.searchStringForVideos,
				selectedStudio: state.selectedStudio,
				selectedCategory: state.selectedCategory,
				isUnassignedVideos: state.isUnassignedVideos,
				sortBy: state.sortBy,
				IDsSelectedVideos: state.IDsSelectedVideos,
				activeItemToDisplay: state.activeItemToDisplay,
				isShowBreadcrumbs: state.isShowBreadcrumbs,
				allCustomers: state.allCustomers,
				IDsSelectedCustomers: state.IDsSelectedCustomers,
				restaurants: state.restaurants,
				IDsSelectedRestaurants: state.IDsSelectedRestaurants,
				videos: state.videos,
				typeOfModalWindow: state.typeOfModalWindow,
				currentPage: state.currentPage,
				countOfItems: state.countOfItems,
			}}>
			{children}
		</VideosAssignmentContext.Provider>
	);
};

export default VideosAssignmentState;
